import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell.tsx';
import { setUnauthorizedHandler } from './lib/api.ts';
import { initAnalytics } from './lib/analytics.ts';
import { useAuth } from './store/auth.ts';
import { LoginPage } from './features/auth/LoginPage.tsx';
import { HomePage } from './features/overview/HomePage.tsx';
import { HealthPage } from './features/health/HealthPage.tsx';
import { HealthReportsPage } from './features/health/HealthReportsPage.tsx';
import { EnterprisePage } from './features/enterprise/EnterprisePage.tsx';
import { WealthPage } from './features/wealth/WealthPage.tsx';
import { ServicePage } from './features/service/ServicePage.tsx';
import { SuccessionPage } from './features/succession/SuccessionPage.tsx';
import { AiPage } from './features/ai/AiPage.tsx';
import { SkPage } from './features/sk/SkPage.tsx';
import { ProfilePage } from './features/profile/ProfilePage.tsx';
import { ConsentPage } from './features/profile/ConsentPage.tsx';
import { ProviderConsentPage } from './features/profile/ProviderConsentPage.tsx';
import { PoliciesPage } from './features/profile/PoliciesPage.tsx';
import { AuditPage } from './features/profile/AuditPage.tsx';

export default function App() {
  const ready = useAuth((s) => s.ready);
  const member = useAuth((s) => s.member);
  const bootstrap = useAuth((s) => s.bootstrap);

  useEffect(() => {
    // 埋点在这里初始化（而不是外壳里）：登录页不属于外壳，
    // 而「登录页停留 → 获取验证码 → 登录成功」恰恰是最需要看的漏斗第一环。
    initAnalytics();
    // token 失效时（服务端吊销 / 过期）自动退回登录页
    setUnauthorizedHandler(() => {
      useAuth.setState({ member: null, consents: [], ready: true });
    });
    void bootstrap();
  }, [bootstrap]);

  if (!ready) {
    return (
      <div className="app-root">
        <div className="stage">
          <div className="state-box">
            <div className="spinner" />
            <div>正在校验登录状态…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={member ? <Navigate to="/" replace /> : <LoginPage />} />

      {/* 未登录时任何页面都回到登录页（真实产品的权限守卫，原型完全没有） */}
      <Route element={member ? <AppShell /> : <Navigate to="/login" replace />}>
        {/* 默认入口是智能体「叁」（口径见 @hew3/shared 的 navigationCaliber：叁为第一入口）。
            原首页（三螺旋总览）不作废，改挂到 /home，从「三康」页与「我的」页都可进入。 */}
        <Route path="/" element={<AiPage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/sk" element={<SkPage />} />
        <Route path="/health" element={<HealthPage />} />
        <Route path="/health/reports" element={<HealthReportsPage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/wealth" element={<WealthPage />} />
        <Route path="/service" element={<ServicePage />} />
        <Route path="/succession" element={<SuccessionPage />} />
        <Route path="/ai" element={<AiPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/consent" element={<ConsentPage />} />
        {/* 服务方授权：粒度是「哪一家机构」×「哪个数据域」，与上一屏的
            「平台 / 专家 / AI 能不能用该数据域」是两件事，因此是一个独立页面而不是同一页的区块。 */}
        <Route path="/profile/provider-consent" element={<ProviderConsentPage />} />
        <Route path="/profile/policies" element={<PoliciesPage />} />
        <Route path="/profile/audit" element={<AuditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
